import {AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {ChainResponsibilityService} from 'src/app/services/chain-responsibility/chain-responsibility.service';
import {ChainSenders} from 'src/app/services/chain-responsibility/chain.interface';
import {MenuService} from 'src/app/services/menu.service';
import {CurrentUiStoreService} from 'src/app/services/ui/current-ui-store.service';
import {AsgardFeatureNames} from 'src/models/AsgardFeatures';
import {CurrentUIState} from 'src/models/UIState';
import {SettingsService} from '../../services/settings/settings.service';
import { MainClickDirectiveDirective } from '../../utils/directives/main-click-directive.directive';
import { TooltipDirective } from '../../utils/directives/tooltip.directive';
import { NgClass, NgTemplateOutlet } from '@angular/common';

enum DisplayStyle {
  none = 'none',
  block = 'block',
  flex = 'flex',
}

@Component({
    selector: 'asg-floating-menu',
    templateUrl: './floating-menu.component.html',
    styleUrls: ['./floating-menu.component.scss'],
    standalone: true,
    imports: [NgClass, NgTemplateOutlet, TooltipDirective, MainClickDirectiveDirective]
})

export class FloatingMenuComponent implements AfterViewInit, OnDestroy {

  constructor(
    private uiService: CurrentUiStoreService,
    private readonly chain: ChainResponsibilityService,
    private readonly menuService: MenuService,
    private readonly settingsService: SettingsService
  ) {
  }

  @ViewChild('toolbar') toolbar: ElementRef;

  @Input() menuItem: AsgardFeatureNames | null = null;
  @Input() image: string;
  @Input() parentItem: AsgardFeatureNames | null = null;

  isDocked = false;
  isHorizontal = false;
  isDockOpen = false;

  private readonly subscriptions: Subscription = new Subscription();
  private isOpen = false;
  private isDragging = false;
  private wasHorizontalBeforeDocked = false;
  private dx = 0;
  private dy = 0;
  private horizontalToolbarCSS = 'floating-menu-toolbar__horizontal';
  private dockedToolbarCSS = 'floating-menu-toolbar__docked';

  private disabledFeaturesWhileDraggin: AsgardFeatureNames[] = [
    AsgardFeatureNames.DROP_DOWN_MENUS
  ];

  darkMode = false;

  @HostListener('window:resize', ['$event'])
  moveIntoView(): void {
    if (!this.isInViewport(this.toolbar.nativeElement)) {
      this.toolbar.nativeElement.style.top = 0 + 'px';
      this.toolbar.nativeElement.style.left = 0 + 'px';
    }
  }

  ngAfterViewInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  isInViewport(element): boolean {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom < (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right < (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  show(): void {
    this.toolbar.nativeElement.style.display =
      !this.isHorizontal && !this.isDocked ? DisplayStyle.block : DisplayStyle.flex;
    // bounce toolbar to top left when it is partially (or fully) out of view in resized or mobile mode
    if (!this.isInViewport(this.toolbar.nativeElement)) {
      this.toolbar.nativeElement.style.top = 0 + 'px';
      this.toolbar.nativeElement.style.left = 0 + 'px';
    }
  }

  hide(): void {
    this.toolbar.nativeElement.style.display = DisplayStyle.none;
  }

  close = (): void => {
    if (this.menuItem) {
      this.uiService.setItemUnActive(this.menuItem);
    }
  }

  doubleClick(): void {
    // don't change oriëntation when menu is docked
    if (!this.isDocked) {
      this.isHorizontal = !this.isHorizontal;
      this.isHorizontal ? this.setHorizontalToolbar() : this.setVerticalToolbar();
    }
  }

  click(): void {
    this.isDockOpen ? this.closeDock() : this.openDock();
  }

  async mouseDown(e: MouseEvent): Promise<void> {
    if ((e.target as HTMLElement).className === 'fas fa-times') {
      e.stopPropagation();
      await this.chain.handleClickEvent(ChainSenders.UI, this.menuItem, null, null, e, this.close, null);
      return;
    }

    this.isDragging = true;
    if (this.isDragging) {
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      this.dx = rect.left - e.x;
      this.dy = rect.top - e.y;
      this.uiService.deattachPointerEventsOnViewerAndEditorsFrames(true);
    }
  }

  mouseMove(evt: PointerEvent): void {
    if (this.isDragging) {
      this.uiService.disableState(this.disabledFeaturesWhileDraggin);
      const clientWidth = document.body.clientWidth;
      const clientHeight = document.body.clientHeight;
      const minX = clientWidth - this.toolbar.nativeElement.clientWidth;
      const minY = clientHeight - this.toolbar.nativeElement.clientHeight;
      let topNumber = 0;
      let leftNumber = 0;

      topNumber = evt.y >= 0 ? evt.y > minY ? minY + this.dy : evt.y + this.dy : 0;
      leftNumber = evt.x >= 0 ? evt.x > minX ? minX + this.dx : evt.x + this.dx : 0;

      leftNumber <= 0 ? this.dock() : this.undock();

      if (!this.isDocked) {
        requestAnimationFrame(() => {
          this.toolbar.nativeElement.style.top = `${topNumber}px`;
          this.toolbar.nativeElement.style.left = `${leftNumber}px`;
          this.toolbar.nativeElement.style.right = `unset`;
        });
      }
    }
  }

  mouseUp(): void {
    if (this.isDragging) {
      this.dx = 0;
      this.dy = 0;
      this.isDragging = false;
      this.uiService.deattachPointerEventsOnViewerAndEditorsFrames(false);
      this.uiService.enableState(this.disabledFeaturesWhileDraggin);
    }
  }

  // @HostListener('document:mouseup', ['$event'])
  // @HostListener('document:touchend', ['$event'])
  @HostListener('document:pointerup ', ['$event'])
  onmouseUp(): void {
    this.mouseUp();
  }

  // @HostListener('document:mousemove', ['$event'])
  // @HostListener('document:touchmove', ['$event'])
  @HostListener('document:pointermove', ['$event'])
  onmouseMove(evt: PointerEvent): void {
    this.mouseMove(evt);
  }

  private init(): void {
    this.applyFloatingStyles();
    this.applyClickEventForContent();
    this.subscribeToMenuActions();

    // set icon for docked control area
    if (this.menuItem) {
      const feature = this.menuService.getMenuByFeature(this.menuItem);
      if (feature && feature.icon) {
        this.image = feature.icon;
      }
    }

    // by default, don't show a floating toolbar
    this.hide();
  }

  private applyFloatingStyles(): void {
    const parent = document.getElementById(`${this.parentItem ? this.parentItem : this.menuItem}`);
    let parentStyle: DOMRect | null = null;
    let cssText: string | null = '';
    let menuSize: number | null = 0;
    if (parent) {
      parentStyle = parent.getBoundingClientRect() as DOMRect;
      if (parentStyle !== null) {
        menuSize = (this.toolbar.nativeElement.getBoundingClientRect().width * parentStyle.width / 2) / parentStyle.width / 2;
      }
    }
    if (this.menuItem === AsgardFeatureNames.WORD_PREDICTION_DIALOG) {
      cssText = `position: fixed;z-index:95;top: ${parentStyle ? parentStyle.height + 5 : 120}px;
    right:${parentStyle ? parentStyle.right + menuSize : 55}px;background: var(--secondary);`;
    } else {
      let left = 0;

      if (parentStyle) {
        left = parentStyle.left;

        if (left > window.outerWidth) {
          left = window.outerWidth - this.toolbar.nativeElement.getBoundingClientRect().width;
        }
      }

      cssText = `position: fixed;z-index:95;top: ${parentStyle ? parentStyle.height + 5 : 0}px;
    left:${left}px;background: var(--secondary);`;
    }

    this.toolbar.nativeElement.style.cssText = cssText;
  }

  private setVerticalToolbar(): void {
    this.toolbar.nativeElement.style.display = DisplayStyle.block;
    if (this.toolbar.nativeElement.classList.contains(this.horizontalToolbarCSS)) {
      this.toolbar.nativeElement.classList.remove(this.horizontalToolbarCSS);
    }
  }

  private setHorizontalToolbar(): void {
    this.toolbar.nativeElement.style.display = DisplayStyle.flex;
    if (!this.toolbar.nativeElement.classList.contains(this.horizontalToolbarCSS)) {
      this.toolbar.nativeElement.classList.add(this.horizontalToolbarCSS);
    }
  }

  private subscribeToMenuActions(): void {
    this.subscriptions.add(
      this.uiService.getUIState()
        .subscribe(
          (e: CurrentUIState) => {
            if (this.menuItem && e) {
              if (e[this.menuItem].active !== this.isOpen) {
                this.isOpen = e[this.menuItem].active;
                if (this.isOpen) {
                  this.show();
                } else {
                  this.hide();
                }
              }
            }
          }
        )
    );
    this.subscriptions.add(
      this.settingsService.getSettingsObservable().subscribe(
        settings => {
          this.darkMode = settings.darkMode;
        }
      )
    );
  }

  private applyClickEventForContent(): void {
    const listItems: HTMLElement[] = this.toolbar.nativeElement.querySelectorAll('li');
    for (const item of listItems) {
      item.onclick = () => {
        if (this.isDocked) {
          this.closeDock();
        }
      };
    }
  }

  private dock(): void {
    // if element isn't docked yet
    if (!this.isDocked) {
      this.isDocked = true;

      // if the toolbars oriëntation is horizontal, remove it
      this.wasHorizontalBeforeDocked = this.isHorizontal;
      if (this.isHorizontal) {
        this.setVerticalToolbar();
        this.isHorizontal = false;
      }

      // add the dock css
      if (!this.toolbar.nativeElement.classList.contains(this.dockedToolbarCSS)) {
        this.toolbar.nativeElement.classList.add(this.dockedToolbarCSS);
      }

      // close the dock by default
      this.closeDock();

      // set left to 0 (because it could also be -something)
      this.toolbar.nativeElement.style.left = '0px';

      // set the toolbar to display flex
      this.toolbar.nativeElement.style.display = DisplayStyle.flex;
    }
  }

  private undock(): void {
    if (this.isDocked && this.isDockOpen) {
      this.isDocked = false;

      // remove the dock css
      this.toolbar.nativeElement.classList.remove(this.dockedToolbarCSS);
      const open = `${this.dockedToolbarCSS}__open`;
      this.toolbar.nativeElement.classList.remove(open);
      const closed = `${this.dockedToolbarCSS}__closed`;
      this.toolbar.nativeElement.classList.remove(closed);

      // set the toolbar back to it's original oriëntation before it was docked
      // doubleClick inverts the boolean and then sets the correct css.
      this.isHorizontal = this.wasHorizontalBeforeDocked;
      this.isHorizontal ? this.setHorizontalToolbar() : this.setVerticalToolbar();
    }
  }

  private openDock(): void {
    const open = `${this.dockedToolbarCSS}__open`;
    const closed = `${this.dockedToolbarCSS}__closed`;

    // if it contains the closed, remove it
    if (this.toolbar.nativeElement.classList.contains(closed)) {
      this.toolbar.nativeElement.classList.remove(closed);
    }

    // if it does not contain the open yet, add it
    if (!this.toolbar.nativeElement.classList.contains(open)) {
      this.toolbar.nativeElement.classList.add(open);
    }

    this.isDockOpen = true;
  }

  private closeDock(): void {
    const open = `${this.dockedToolbarCSS}__open`;
    const closed = `${this.dockedToolbarCSS}__closed`;

    // if it contains the open, remove it
    if (this.toolbar.nativeElement.classList.contains(open)) {
      this.toolbar.nativeElement.classList.remove(open);
    }

    // if it does not contain the close yet, add it
    if (!this.toolbar.nativeElement.classList.contains(closed)) {
      this.toolbar.nativeElement.classList.add(closed);
    }

    this.isDockOpen = false;
  }
}
